import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { ShoppingCart, Eye, Printer, FileText, Trash2 } from "lucide-react";
import {
  Box,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Grid,
  Divider,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
} from "@mui/material";
import DataTable from "@/components/mui/DataTable";

export default function Sales() {
  const [sales, setSales] = useState([]);
  const [customers, setCustomers] = useState([]);

  const [selectedSale, setSelectedSale] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  // ABA atual: "sales" (Vendas) ou "budgets" (Orçamentos)
  const [viewMode, setViewMode] = useState("sales");

  // Filtros
  const [filters, setFilters] = useState({
    customer_id: "",
    status: "",
    payment_method: "",
    date_from: "",
    date_to: "",
  });

  // Ordenação
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");

  // Modal de senha supervisor/adm
  const [supervisorDialogOpen, setSupervisorDialogOpen] = useState(false);
  const [supervisorPassword, setSupervisorPassword] = useState("");
  const [validatingSupervisor, setValidatingSupervisor] = useState(false);

  // Modal de confirmação de devolução
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [saleToRefund, setSaleToRefund] = useState(null);
  const [refunding, setRefunding] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [budgetToCancel, setBudgetToCancel] = useState(null);
  const [approvingBudget, setApprovingBudget] = useState(false);
  const [cancellingBudget, setCancellingBudget] = useState(false);
  const [emittingNfeSaleId, setEmittingNfeSaleId] = useState(null);

  // Modal de aprovação de orçamento
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [budgetToApprove, setBudgetToApprove] = useState(null);
  const [approving, setApproving] = useState(false);
  const [approveData, setApproveData] = useState({
    payment_method: 'cash',
    payment_details: '',
    promissory_data: {
      parcelar: false,
      parcelas: 1,
      datas: [],
    },
  });

  // Paginação client-side
  const [page, setPage] = useState(0); // 0-based
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    fetchCustomers();
  }, []);

  // Sempre que trocar entre Vendas / Orçamentos, recarrega lista
  useEffect(() => {
    fetchSales();
  }, [viewMode]);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`, {
        params: { per_page: 9999 },
      });
      setCustomers(response.data.data || response.data);
    } catch (error) {
      console.error("Erro ao carregar clientes", error);
    }
  };

  /**
   * Busca vendas (ou orçamentos) com filtros + ordenação.
   * Se não passar nada, usa estado atual de filters e sort.
   */
  const fetchSales = async (
    filtersArg = filters,
    sortInfo = { sortBy, sortDir }
  ) => {
    try {
      setLoading(true);

      const isBudget = viewMode === "budgets" ? 1 : 0;

      const params = {
        customer_id: filtersArg.customer_id || undefined,
        status: filtersArg.status || undefined,
        payment_method: filtersArg.payment_method || undefined,
        date_from: filtersArg.date_from || undefined,
        date_to: filtersArg.date_to || undefined,
        sort_by: sortInfo.sortBy,
        sort_dir: sortInfo.sortDir,
        per_page: -1, // backend retorna tudo; paginamos no front
        is_budget: isBudget, // <<< separa vendas x orçamentos
      };

      const response = await axios.get(`${API}/sales`, { params });
      setSales(response.data.data || response.data);
    } catch (error) {
      toast.error("Erro ao carregar registros");
    } finally {
      setLoading(false);
    }
  };

  const viewSale = async (saleId) => {
    try {
      const response = await axios.get(`${API}/sales/${saleId}`);
      setSelectedSale(response.data);
      setShowModal(true);
    } catch (error) {
      toast.error("Erro ao carregar detalhes");
    }
  };

  // Filtros – handlers
  const handleFilterChange = (field) => (event) => {
    const value = event.target.value;
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleApplyFilters = () => {
    setPage(0);
    fetchSales(filters, { sortBy, sortDir });
  };

  const handleClearFilters = () => {
    const cleared = {
      customer_id: "",
      status: "",
      payment_method: "",
      date_from: "",
      date_to: "",
    };
    setFilters(cleared);
    setPage(0);
    setSortBy("created_at");
    setSortDir("desc");
    fetchSales(cleared, { sortBy: "created_at", sortDir: "desc" });
  };

  // Ordenação – clique no header
  const handleRequestSort = (property) => {
    const isAsc = sortBy === property && sortDir === "asc";
    const newDir = isAsc ? "desc" : "asc";

    setSortBy(property);
    setSortDir(newDir);

    fetchSales(filters, { sortBy: property, sortDir: newDir });
  };

  // Clique em "Devolver" (apenas em Vendas)
  const handleRefundClick = (sale) => {
    setSaleToRefund(sale);
    setSupervisorPassword("");
    setSupervisorDialogOpen(true);
  };

  // 1ª etapa: validar senha de supervisor/admin
  const handleSupervisorContinue = async () => {
    if (!supervisorPassword) {
      toast.error("Informe a senha do supervisor/administrador.");
      return;
    }

    try {
      setValidatingSupervisor(true);

      await axios.post(`${API}/users/check-supervisor-password`, {
        password: supervisorPassword,
      });

      // senha OK -> abre modal de confirmação
      setSupervisorDialogOpen(false);
      setRefundDialogOpen(true);
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        "Senha inválida para supervisor/administrador.";
      toast.error(msg);
    } finally {
      setValidatingSupervisor(false);
    }
  };

  // 2ª etapa: confirmar devolução (chama /sales/{id}/refund)
  const handleConfirmRefund = async () => {
    if (!saleToRefund) return;

    if (!supervisorPassword) {
      toast.error("Senha do supervisor não informada.");
      return;
    }

    try {
      setRefunding(true);

      await axios.post(`${API}/sales/${saleToRefund.id}/refund`, {
        supervisor_password: supervisorPassword,
      });

      toast.success("Venda devolvida com sucesso!");

      closeRefundDialog();
      fetchSales(filters, { sortBy, sortDir });
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        "Erro ao processar devolução";
      toast.error(msg);
    } finally {
      setRefunding(false);
    }
  };

  const handleApproveBudget = async () => {
    if (!budgetToApprove) return;
    try {
      setApprovingBudget(true);
      await axios.post(`${API}/sales/${budgetToApprove.id}/approve-budget`);
      toast.success("Orçamento aprovado com sucesso!");
      setApproveDialogOpen(false);
      setBudgetToApprove(null);
      fetchSales(filters, { sortBy, sortDir });
    } catch (error) {
      const msg =
        error.response?.data?.message || "Erro ao aprovar orçamento";
      toast.error(msg);
    } finally {
      setApprovingBudget(false);
    }
  };

  const handleCancelBudget = async () => {
    if (!budgetToCancel) return;
    try {
      setCancellingBudget(true);
      await axios.delete(`${API}/sales/${budgetToCancel.id}/cancel-budget`);
      toast.success("Orçamento cancelado com sucesso!");
      setCancelDialogOpen(false);
      setBudgetToCancel(null);
      fetchSales(filters, { sortBy, sortDir });
    } catch (error) {
      const msg =
        error.response?.data?.message || "Erro ao cancelar orçamento";
      toast.error(msg);
    } finally {
      setCancellingBudget(false);
    }
  };

  const handleDeleteSale = async (sale) => {
    if (!sale) return;

    const confirmed = window.confirm(
      `Deseja remover a venda #${sale.id}? Essa ação não pode ser desfeita.`,
    );

    if (!confirmed) return;

    try {
      await axios.delete(`${API}/sales/${sale.id}`);
      toast.success("Venda removida com sucesso!");

      if (selectedSale?.id === sale.id) {
        setShowModal(false);
        setSelectedSale(null);
      }

      fetchSales(filters, { sortBy, sortDir });
    } catch (error) {
      const msg =
        error.response?.data?.message || "Erro ao remover venda";
      toast.error(msg);
    }
  };

  const closeRefundDialog = () => {
    setRefundDialogOpen(false);
    setSaleToRefund(null);
  };

  // ============ APROVAÇÃO DE ORÇAMENTO ============
  const openApproveDialog = (budget) => {
    setBudgetToApprove(budget);
    setApproveData({
      payment_method: 'cash',
      payment_details: '',
      promissory_data: {
        parcelar: false,
        parcelas: 1,
        datas: [new Date().toISOString().split('T')[0]],
      },
    });
    setApproveDialogOpen(true);
  };

  const handleApproveDataChange = (field, value) => {
    setApproveData(prev => ({ ...prev, [field]: value }));
  };

  const handlePromissoryChange = (field, value) => {
    setApproveData(prev => ({
      ...prev,
      promissory_data: { ...prev.promissory_data, [field]: value },
    }));

    // Se alterou número de parcelas, gerar datas mensais
    if (field === 'parcelas') {
      const datas = [];
      const today = new Date();
      for (let i = 0; i < value; i++) {
        const date = new Date(today);
        date.setMonth(date.getMonth() + i);
        datas.push(date.toISOString().split('T')[0]);
      }
      setApproveData(prev => ({
        ...prev,
        promissory_data: { ...prev.promissory_data, parcelas: value, datas },
      }));
    }
  };

  const handleConfirmApprove = async () => {
    if (!budgetToApprove) return;

    try {
      setApproving(true);
      await axios.post(`${API}/sales/${budgetToApprove.id}/approve-budget`, approveData);
      toast.success("Orçamento aprovado com sucesso!");
      setApproveDialogOpen(false);
      setBudgetToApprove(null);
      fetchSales(filters, { sortBy, sortDir });
    } catch (error) {
      toast.error(error.response?.data?.message || "Erro ao aprovar orçamento");
    } finally {
      setApproving(false);
    }
  };

  const getPaymentMethodLabel = (method) => {
    const methods = {
      cash: "Dinheiro",
      credit: "Crédito",
      debit: "Débito",
      pix: "PIX",
      promissoria: "Promissória",
    };
    return methods[method] || method;
  };

  const formatMoney = (value) =>
    `R$ ${Number(value || 0).toFixed(2).replace(".", ",")}`;

  // Imprimir comprovante (abre janela de impressão)
  const printReceipt = (receipt) => {
    const printWindow = window.open("", "_blank", "width=400,height=600");
    if (!printWindow) return;

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Comprovante</title>
        <style>
          body { font-family: 'Courier New', monospace; font-size: 12px; padding: 10px; max-width: 300px; margin: 0 auto; }
          .center { text-align: center; }
          .bold { font-weight: bold; }
          .divider { border-top: 1px dashed #000; margin: 8px 0; }
          table { width: 100%; }
          td { padding: 2px 0; }
          .right { text-align: right; }
        </style>
      </head>
      <body>
        <div class="center bold">${receipt.empresa?.nome || "EMPRESA"}</div>
        <div class="center">${receipt.empresa?.cnpj || ""}</div>
        <div class="center">${receipt.empresa?.endereco || ""}</div>
        <div class="center">${receipt.empresa?.telefone || ""}</div>
        <div class="divider"></div>
        <div class="center bold">CUPOM NÃO FISCAL</div>
        <div>Venda #${receipt.venda?.numero}</div>
        <div>Data: ${receipt.venda?.data}</div>
        <div>Vendedor: ${receipt.venda?.vendedor || "-"}</div>
        ${receipt.cliente ? `<div>Cliente: ${receipt.cliente.nome}</div>` : ""}
        <div class="divider"></div>
        <table>
          ${receipt.itens
            ?.map(
              (item) => `
            <tr>
              <td colspan="2">${item.descricao}</td>
            </tr>
            <tr>
              <td>${item.quantidade} x R$ ${item.valor_unitario}</td>
              <td class="right">R$ ${item.subtotal}</td>
            </tr>
          `
            )
            .join("")}
        </table>
        <div class="divider"></div>
        <table>
          <tr><td>Subtotal:</td><td class="right">R$ ${receipt.totais?.subtotal}</td></tr>
          <tr><td>Desconto:</td><td class="right">R$ ${receipt.totais?.desconto}</td></tr>
          <tr class="bold"><td>TOTAL:</td><td class="right">R$ ${receipt.totais?.total}</td></tr>
        </table>
        <div class="divider"></div>
        <div>Pagamento: ${receipt.pagamento?.forma}</div>
        <div class="divider"></div>
        <div class="center">Obrigado pela preferência!</div>
        <script>window.print();</script>
      </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
  };

  const handlePrintReceipt = async (sale) => {
    try {
      const receiptRes = await axios.get(`${API}/sales/${sale.id}/receipt`);
      printReceipt(receiptRes.data);
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Erro ao imprimir comprovante"
      );
    }
  };

  const printFiscalPdf = (pdfBase64, title = "NF-e") => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      toast.error("Permita pop-ups para imprimir a NF-e.");
      return;
    }

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${title}</title>
        <style>
          html, body { margin: 0; height: 100%; }
          iframe { border: 0; width: 100%; height: 100%; }
        </style>
      </head>
      <body>
        <iframe id="pdf" src="data:application/pdf;base64,${pdfBase64}"></iframe>
        <script>
          const iframe = document.getElementById('pdf');
          iframe.onload = () => setTimeout(() => window.print(), 300);
        </script>
      </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
  };

  const handleEmitOrPrintNfe = async (sale) => {
    try {
      setEmittingNfeSaleId(sale.id);

      const actionRes = await axios.post(`${API}/sales/${sale.id}/emit-or-print-nfe`);
      const fiscalDocumentId = actionRes.data?.fiscal_document_id;

      if (!fiscalDocumentId) {
        throw new Error("Documento fiscal não localizado para impressão.");
      }

      const pdfRes = await axios.get(`${API}/fiscal/documents/${fiscalDocumentId}/pdf`);
      const pdfBase64 = pdfRes.data?.pdf_base64;

      if (!pdfBase64) {
        throw new Error("PDF da NF-e não retornado pela API.");
      }

      printFiscalPdf(pdfBase64, pdfRes.data?.filename || `nfe_${fiscalDocumentId}.pdf`);
      toast.success(actionRes.data?.message || "NF-e pronta para impressão.");
      fetchSales(filters, { sortBy, sortDir });
    } catch (error) {
      const msg =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        "Erro ao emitir/imprimir NF-e";
      toast.error(msg);
    } finally {
      setEmittingNfeSaleId(null);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const isBudgetsView = viewMode === "budgets";

  // colunas para o DataTable
  const columns = [
    {
      id: "created_at",
      label: "Data",
      sortable: true,
      render: (sale) =>
        new Date(sale.created_at).toLocaleString("pt-BR"),
    },
    {
      id: "customer_name",
      label: "Cliente",
      render: (sale) =>
        sale.customer?.name || "Cliente não informado",
    },
    {
      id: "items",
      label: "Itens",
      align: "center",
      render: (sale) => sale.items?.length ?? 0,
    },
    {
      id: "subtotal",
      label: "Subtotal",
      align: "right",
      sortable: true,
      render: (sale) => formatMoney(sale.subtotal),
    },
    {
      id: "discount",
      label: "Desconto",
      align: "right",
      sortable: true,
      render: (sale) => formatMoney(sale.discount),
    },
    {
      id: "total",
      label: "Total",
      align: "right",
      sortable: true,
      render: (sale) => (
        <Typography fontWeight={700}>
          {formatMoney(sale.total)}
        </Typography>
      ),
    },
    {
      id: "payment_method",
      label: "Pagamento",
      sortable: true,
      render: (sale) => (
        <Chip
          label={getPaymentMethodLabel(sale.payment_method)}
          color="info"
          size="small"
        />
      ),
    },
    {
      id: "status",
      label: "Status",
      sortable: true,
      render: (sale) => (
        <Chip
          label={
            sale.status === "refunded"
              ? "Devolvida"
              : sale.status === "completed"
                ? "Concluída"
                : sale.status === "pending"
                  ? "Pendente"
                  : sale.status === "cancelled"
                    ? "Cancelada"
                    : sale.status
          }
          size="small"
          color={
            sale.status === "refunded"
              ? "warning"
              : sale.status === "completed"
                ? "success"
                : sale.status === "cancelled"
                  ? "error"
                  : "default"
          }
        />
      ),
    },
    {
      id: "actions",
      label: "Ações",
      render: (sale) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Eye size={14} />}
            onClick={() => viewSale(sale.id)}
            data-testid={`view-sale-${sale.id}`}
          >
            Ver
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Printer size={14} />}
            onClick={() => handlePrintReceipt(sale)}
          >
            Imprimir
          </Button>

          {!isBudgetsView && sale.status !== "refunded" && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<FileText size={14} />}
              onClick={() => handleEmitOrPrintNfe(sale)}
              disabled={emittingNfeSaleId === sale.id || sale.status !== "completed"}
            >
              {emittingNfeSaleId === sale.id ? "Emitindo..." : "Emitir NF-e"}
            </Button>
          )}

          {/* Botão Devolver só na aba de VENDAS */}
          {!isBudgetsView && sale.status !== "refunded" && (
            <Button
              variant="outlined"
              size="small"
              color="error"
              disabled={sale.status === "refunded"}
              onClick={() => handleRefundClick(sale)}
            >
              Devolver
            </Button>
          )}

          {!isBudgetsView && sale.status === "refunded" && (
            <Button
              variant="outlined"
              size="small"
              color="error"
              startIcon={<Trash2 size={14} />}
              onClick={() => handleDeleteSale(sale)}
            >
              Remover
            </Button>
          )}

          {isBudgetsView && (
            <>
              <Button
                variant="outlined"
                size="small"
                color="success"
                disabled={sale.status !== "pending"}
                onClick={() => {
                  setBudgetToApprove(sale);
                  setApproveDialogOpen(true);
                }}
              >
                Aprovar
              </Button>
              <Button
                variant="outlined"
                size="small"
                color="error"
                disabled={sale.status !== "pending"}
                onClick={() => {
                  setBudgetToCancel(sale);
                  setCancelDialogOpen(true);
                }}
              >
                Cancelar
              </Button>
            </>
          )}
        </Box>
      ),
    },
  ];

  const itemColumns = [
    {
      id: "product",
      label: "Produto",
      render: (item) => item.product?.name || "-",
    },
    {
      id: "quantity",
      label: "Qtd",
      align: "center",
    },
    {
      id: "unit_price",
      label: "Preço Unit.",
      align: "left",
      render: (item) => formatMoney(item.unit_price),
    },
    {
      id: "total",
      label: "Total",
      align: "right",
      render: (item) => (
        <Typography fontWeight={700}>
          {formatMoney(item.total)}
        </Typography>
      ),
    },
  ];

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: 200,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const paginatedSales =
    rowsPerPage > 0
      ? sales.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      : sales;

  const summarySales =
    isBudgetsView ? sales : sales.filter((s) => s.status !== "refunded");

  const totalSales = summarySales.length;
  const totalRevenue = summarySales.reduce(
    (sum, s) => sum + Number(s.total || 0),
    0
  );
  const totalDiscount = summarySales.reduce(
    (sum, s) => sum + Number(s.discount || 0),
    0
  );
  const avgTicket =
    totalSales > 0 ? totalRevenue / totalSales : 0;

  return (
    <Box data-testid="sales-page">
      <Paper
        sx={{
          mb: 3,
          borderRadius: 2,
          overflow: "hidden",
          boxShadow: "0 10px 40px rgba(15,23,42,0.04)",
        }}
      >
        {/* Cabeçalho + Abas */}
        <Box
          sx={{
            borderBottom: "1px solid",
            borderColor: "divider",
            px: 3,
            py: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={600}>
              {isBudgetsView ? "Orçamentos" : "Vendas"}
            </Typography>
            {sales.length > 0 && (
              <Typography variant="body2" color="text.secondary">
                {totalSales}{" "}
                {isBudgetsView
                  ? "orçamento(s) registrados"
                  : "venda(s) registradas"}
              </Typography>
            )}
          </Box>

          <Tabs
            value={viewMode}
            onChange={(_, value) => {
              setViewMode(value);
              setPage(0);
            }}
            textColor="primary"
            indicatorColor="primary"
            sx={{
              minHeight: 32,
              "& .MuiTab-root": {
                minHeight: 32,
                fontSize: 13,
              },
            }}
          >
            <Tab label="Vendas" value="sales" />
            <Tab label="Orçamentos" value="budgets" />
          </Tabs>
        </Box>

        {/* Cards de resumo */}
        {sales.length > 0 && (
          <Box
            sx={{
              px: 3,
              pt: 2,
              pb: 1,
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, minmax(0, 1fr))",
                md: "repeat(4, minmax(0, 1fr))",
              },
              gap: 3,
            }}
          >
            <Paper
              sx={{
                p: 3,
                minHeight: 140,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <Typography
                variant="subtitle1"
                color="text.secondary"
                sx={{ fontWeight: 500, mb: 1 }}
              >
                {isBudgetsView ? "Total de Orçamentos" : "Total de Vendas"}
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {totalSales}
              </Typography>
            </Paper>

            <Paper
              sx={{
                p: 3,
                minHeight: 140,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <Typography
                variant="subtitle1"
                color="text.secondary"
                sx={{ fontWeight: 500, mb: 1 }}
              >
                {isBudgetsView
                  ? "Valor Total Orçado"
                  : "Receita Total"}
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  color: "#10b981",
                  fontWeight: 700,
                  fontSize: { xs: "1.8rem", md: "2.1rem" },
                }}
              >
                {formatMoney(totalRevenue)}
              </Typography>
            </Paper>

            <Paper
              sx={{
                p: 3,
                minHeight: 140,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <Typography
                variant="subtitle1"
                color="text.secondary"
                sx={{ fontWeight: 500, mb: 1 }}
              >
                Total de Descontos
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  color: "error.main",
                  fontWeight: 700,
                  fontSize: { xs: "1.8rem", md: "2.1rem" },
                }}
              >
                {formatMoney(totalDiscount)}
              </Typography>
            </Paper>

            <Paper
              sx={{
                p: 3,
                minHeight: 140,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <Typography
                variant="subtitle1"
                color="text.secondary"
                sx={{ fontWeight: 500, mb: 1 }}
              >
                {isBudgetsView ? "Média por orçamento" : "Ticket Médio"}
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "1.8rem", md: "2.1rem" },
                }}
              >
                {formatMoney(avgTicket)}
              </Typography>
            </Paper>
          </Box>
        )}

        {/* Filtros abaixo dos cards */}
        <Box
          sx={{
            px: 3,
            pt: 2,
            pb: 2,
            width: "100%",
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(2, minmax(0, 1fr))",
              sm: "repeat(3, minmax(0, 1fr))",
              md: "repeat(6, minmax(0, 1fr))",
            },
            columnGap: 2,
            rowGap: 2,
            alignItems: "center",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          {/* Cliente */}
          <FormControl fullWidth size="small">
            <InputLabel id="filter-customer-label">Cliente</InputLabel>
            <Select
              labelId="filter-customer-label"
              label="Cliente"
              value={filters.customer_id}
              onChange={handleFilterChange("customer_id")}
            >
              <MenuItem value="">Todos</MenuItem>
              {customers.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Status */}
          <FormControl fullWidth size="small">
            <InputLabel id="filter-status-label">Status</InputLabel>
            <Select
              labelId="filter-status-label"
              label="Status"
              value={filters.status}
              onChange={handleFilterChange("status")}
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="completed">Concluída</MenuItem>
              <MenuItem value="pending">Pendente</MenuItem>
              <MenuItem value="cancelled">Cancelada</MenuItem>
              <MenuItem value="refunded">Devolvida</MenuItem>
            </Select>
          </FormControl>

          {/* Forma de pagamento */}
          <FormControl fullWidth size="small">
            <InputLabel id="filter-payment-label">Pagamento</InputLabel>
            <Select
              labelId="filter-payment-label"
              label="Pagamento"
              value={filters.payment_method}
              onChange={handleFilterChange("payment_method")}
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="cash">Dinheiro</MenuItem>
              <MenuItem value="credit">Crédito</MenuItem>
              <MenuItem value="debit">Débito</MenuItem>
              <MenuItem value="pix">PIX</MenuItem>
            </Select>
          </FormControl>

          {/* Data início */}
          <TextField
            label="Data início"
            type="date"
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={filters.date_from}
            onChange={handleFilterChange("date_from")}
          />

          {/* Data fim */}
          <TextField
            label="Data fim"
            type="date"
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={filters.date_to}
            onChange={handleFilterChange("date_to")}
          />

          {/* Botões */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              gridColumn: { xs: "1 / -1", md: "auto" },
            }}
          >
            <Button
              variant="outlined"
              size="small"
              onClick={handleClearFilters}
            >
              Limpar
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={handleApplyFilters}
            >
              Filtrar
            </Button>
          </Box>
        </Box>

        {/* Conteúdo / tabela */}
        {sales.length === 0 ? (
          <Box
            sx={{
              py: 6,
              px: 3,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              gap: 1.5,
            }}
          >
            <ShoppingCart size={64} />
            <Typography variant="h6">
              {isBudgetsView
                ? "Nenhum orçamento encontrado"
                : "Nenhuma venda encontrada"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Ajuste os filtros ou aguarde novos registros.
            </Typography>
          </Box>
        ) : (
          <DataTable
            columns={columns}
            rows={paginatedSales}
            loading={false}
            pagination={{
              page,
              rowsPerPage,
              total: sales.length,
              onPageChange: handleChangePage,
              onRowsPerPageChange: handleChangeRowsPerPage,
              rowsPerPageOptions: [
                5,
                10,
                25,
                50,
                { label: "Todos", value: -1 },
              ],
            }}
            orderBy={sortBy}
            orderDirection={sortDir}
            onRequestSort={handleRequestSort}
            getRowId={(row) => row.id}
            getRowTestId={(row) => `sale-row-${row.id}`}
            emptyMessage={
              isBudgetsView
                ? "Nenhum orçamento registrado"
                : "Nenhuma venda registrada"
            }
          />
        )}
      </Paper>

      {/* Dialog de senha de supervisor/adm */}
      <Dialog
        open={supervisorDialogOpen}
        onClose={() => {
          if (!validatingSupervisor && !refunding) {
            setSupervisorDialogOpen(false);
            setSaleToRefund(null);
            setSupervisorPassword("");
          }
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Autorização do supervisor</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            Para devolver/cancelar esta venda é necessária a senha de um
            <strong> usuário supervisor ou administrador</strong>.
          </Typography>

          <TextField
            label="Senha do supervisor/administrador"
            type="password"
            fullWidth
            margin="normal"
            value={supervisorPassword}
            onChange={(e) => setSupervisorPassword(e.target.value)}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setSupervisorDialogOpen(false);
              setSaleToRefund(null);
              setSupervisorPassword("");
            }}
            disabled={validatingSupervisor || refunding}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSupervisorContinue}
            disabled={validatingSupervisor || refunding}
          >
            {validatingSupervisor ? "Validando..." : "Continuar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de aprovação do orçamento */}
      <Dialog
        open={approveDialogOpen}
        onClose={() => {
          if (!approvingBudget) {
            setApproveDialogOpen(false);
            setBudgetToApprove(null);
          }
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Aprovar orçamento</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary">
            Ao aprovar, o sistema dará baixa no estoque e converterá o
            orçamento em venda concluída.
          </Typography>
          {budgetToApprove && (
            <Box sx={{ mt: 2, p: 2, borderRadius: 2, bgcolor: "grey.50" }}>
              <Typography variant="subtitle2">
                {budgetToApprove.customer?.name || "Cliente não informado"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Itens: {budgetToApprove.items?.length ?? 0} • Total:{" "}
                {formatMoney(budgetToApprove.total)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setApproveDialogOpen(false);
              setBudgetToApprove(null);
            }}
            disabled={approvingBudget}
          >
            Voltar
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleApproveBudget}
            disabled={approvingBudget}
          >
            {approvingBudget ? "Aprovando..." : "Aprovar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de cancelamento do orçamento */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => {
          if (!cancellingBudget) {
            setCancelDialogOpen(false);
            setBudgetToCancel(null);
          }
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Cancelar orçamento</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary">
            Esta ação remove o orçamento. Se precisar manter histórico,
            podemos mudar para “cancelado” em vez de excluir.
          </Typography>
          {budgetToCancel && (
            <Box sx={{ mt: 2, p: 2, borderRadius: 2, bgcolor: "grey.50" }}>
              <Typography variant="subtitle2">
                {budgetToCancel.customer?.name || "Cliente não informado"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Itens: {budgetToCancel.items?.length ?? 0} • Total:{" "}
                {formatMoney(budgetToCancel.total)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCancelDialogOpen(false);
              setBudgetToCancel(null);
            }}
            disabled={cancellingBudget}
          >
            Voltar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleCancelBudget}
            disabled={cancellingBudget}
          >
            {cancellingBudget ? "Cancelando..." : "Cancelar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de detalhes da venda / orçamento */}
      <Dialog
        open={showModal}
        onClose={() => setShowModal(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {isBudgetsView ? "Detalhes do Orçamento" : "Detalhes da Venda"}
        </DialogTitle>
        <DialogContent dividers>
          {selectedSale && (
            <Box>
              {/* Bloco de informações principais */}
              <Box
                sx={{
                  mb: 2,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: "grey.50",
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>Data:</strong>{" "}
                      {new Date(
                        selectedSale.created_at
                      ).toLocaleString("pt-BR")}
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>Cliente:</strong>{" "}
                      {selectedSale.customer?.name ||
                        "Não informado"}
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>Pagamento:</strong>{" "}
                      {getPaymentMethodLabel(
                        selectedSale.payment_method
                      )}
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>Total:</strong>{" "}
                      <span
                        style={{
                          color: "#0ea5e9",
                          fontWeight: 700,
                        }}
                      >
                        {formatMoney(selectedSale.total)}
                      </span>
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Itens */}
              <Typography
                variant="subtitle1"
                sx={{ mb: 1.5, fontWeight: 600 }}
              >
                Itens
              </Typography>

              <DataTable
                columns={itemColumns}
                rows={selectedSale.items || []}
                loading={false}
                emptyMessage="Nenhum item"
              />

              {/* Totais */}
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: "grey.50",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography>Subtotal:</Typography>
                  <Typography fontWeight={600}>
                    {formatMoney(selectedSale.subtotal)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                    color: "error.main",
                  }}
                >
                  <Typography>Desconto:</Typography>
                  <Typography fontWeight={600}>
                    - {formatMoney(selectedSale.discount)}
                  </Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mt: 1,
                  }}
                >
                  <Typography variant="h6">Total:</Typography>
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    color="primary"
                  >
                    {formatMoney(selectedSale.total)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowModal(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de confirmação de devolução (apenas Vendas) */}
      <Dialog
        open={refundDialogOpen}
        onClose={refunding ? undefined : closeRefundDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Confirmar devolução</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            Deseja realmente devolver/cancelar esta venda?
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Isso irá devolver os itens ao estoque e marcar a venda como{" "}
            <strong>devolvida</strong>.
          </Typography>

          {saleToRefund && (
            <Box sx={{ mt: 2, p: 1.5, borderRadius: 1, bgcolor: "grey.50" }}>
              <Typography variant="body2">
                <strong>Cód. venda:</strong> #{saleToRefund.id}
              </Typography>
              <Typography variant="body2">
                <strong>Total:</strong> {formatMoney(saleToRefund.total)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRefundDialog} disabled={refunding}>
            Cancelar
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmRefund}
            disabled={refunding}
          >
            {refunding ? "Processando..." : "Confirmar devolução"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Aprovação de Orçamento */}
      <Dialog
        open={approveDialogOpen}
        onClose={approving ? undefined : () => setApproveDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Aprovar Orçamento</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Selecione a forma de pagamento para aprovar este orçamento e convertê-lo em venda.
          </Typography>

          {budgetToApprove && (
            <Box sx={{ mb: 3, p: 2, borderRadius: 1, bgcolor: "grey.50" }}>
              <Typography variant="body2">
                <strong>Orçamento:</strong> #{budgetToApprove.id}
              </Typography>
              <Typography variant="body2">
                <strong>Cliente:</strong> {budgetToApprove.customer?.name || 'Não informado'}
              </Typography>
              <Typography variant="body2">
                <strong>Total:</strong> {formatMoney(budgetToApprove.total)}
              </Typography>
            </Box>
          )}

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Forma de Pagamento</InputLabel>
            <Select
              value={approveData.payment_method}
              label="Forma de Pagamento"
              onChange={(e) => handleApproveDataChange('payment_method', e.target.value)}
            >
              <MenuItem value="cash">Dinheiro</MenuItem>
              <MenuItem value="credit">Cartão de Crédito</MenuItem>
              <MenuItem value="debit">Cartão de Débito</MenuItem>
              <MenuItem value="pix">PIX</MenuItem>
              <MenuItem value="promissoria">Promissória</MenuItem>
            </Select>
          </FormControl>

          {/* Campos de Promissória */}
          {approveData.payment_method === 'promissoria' && (
            <Box sx={{ p: 2, borderRadius: 1, bgcolor: 'warning.50', border: '1px solid', borderColor: 'warning.main' }}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>Dados da Promissória</Typography>
              
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Parcelar?</InputLabel>
                <Select
                  value={approveData.promissory_data.parcelar ? 'sim' : 'nao'}
                  label="Parcelar?"
                  onChange={(e) => handlePromissoryChange('parcelar', e.target.value === 'sim')}
                >
                  <MenuItem value="nao">Não - Pagamento único</MenuItem>
                  <MenuItem value="sim">Sim - Parcelar</MenuItem>
                </Select>
              </FormControl>

              {approveData.promissory_data.parcelar && (
                <TextField
                  fullWidth
                  type="number"
                  label="Quantidade de Parcelas"
                  value={approveData.promissory_data.parcelas}
                  onChange={(e) => handlePromissoryChange('parcelas', parseInt(e.target.value) || 1)}
                  inputProps={{ min: 1, max: 24 }}
                  sx={{ mb: 2 }}
                />
              )}

              <Typography variant="body2" sx={{ mb: 1 }}>
                {approveData.promissory_data.parcelar ? 'Datas das parcelas:' : 'Data de vencimento:'}
              </Typography>
              
              {approveData.promissory_data.datas?.map((data, index) => (
                <TextField
                  key={index}
                  fullWidth
                  type="date"
                  label={`Parcela ${index + 1}`}
                  value={data}
                  onChange={(e) => {
                    const newDatas = [...approveData.promissory_data.datas];
                    newDatas[index] = e.target.value;
                    setApproveData(prev => ({
                      ...prev,
                      promissory_data: { ...prev.promissory_data, datas: newDatas },
                    }));
                  }}
                  InputLabelProps={{ shrink: true }}
                  sx={{ mb: 1 }}
                  size="small"
                />
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialogOpen(false)} disabled={approving}>
            Cancelar
          </Button>
          <Button
            color="success"
            variant="contained"
            onClick={handleConfirmApprove}
            disabled={approving}
          >
            {approving ? "Aprovando..." : "Aprovar Orçamento"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
